<template>
  <span v-if="mostrarBotaoProtecao">
    <!-- Cobrir é um clique; revelar pede o código do segundo fator quando o
    motor está ligado. O rótulo segue `dadosMascarados`, e não o estado local,
    porque com o motor ligado o dado já chega coberto mesmo sem ninguém ter
    clicado em nada, e um botão que diga o contrário da tela é pior que nenhum.

    Os dois rótulos são VERBOS e citam o mesmo escopo, de propósito. "Proteger"
    era juízo de valor ao lado de uma ação, e levava à pergunta "então antes não
    estava protegido?". "Anonimizar" sugeriria um estado definitivo, e este é
    reversível por quem tem o código. -->
    <button
      type="button"
      :class="[classe, { 'pulso-protecao': deveChamarAtencao }]"
      @click="togglePresentation"
      :disabled="telaBloqueada"
      :title="textoDeApoio"
    >
      <i :class="dadosMascarados ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
      {{ dadosMascarados ? "Exibir dados pessoais" : "Ocultar dados pessoais" }}
    </button>

    <modal-verificacao-acao ref="verificacaoAcao"></modal-verificacao-acao>
  </span>
</template>

<script>
/**
 * Controle de proteção dos dados pessoais (WEL-5343 / WEL-5344).
 *
 * Componente, e não código repetido em cada tela, por lição desta entrega: a
 * mesma regra escrita em dois lugares foi a origem de quase todo o
 * comportamento errático que apareceu na validação. Toda tela que precisar do
 * controle usa este, e o comportamento não tem como divergir de si mesmo.
 *
 * Existe fora do cabeçalho porque nem toda página tem cabeçalho, e o dado chega
 * coberto em todas elas: sem o controle na tela, o usuário fica sem saída.
 */
import { mapGetters, mapActions } from "vuex";
import Swal from "sweetalert2";
import ApiService from "@/core/services/api.service";
import ModalVerificacaoAcao from "@/view/components/ModalVerificacaoAcao.vue";
import { UPDATE_PERSONAL_INFO } from "@/core/services/store/profile.module";

export default {
  name: "BotaoProtecaoDados",
  components: { "modal-verificacao-acao": ModalVerificacaoAcao },

  props: {
    classe: {
      type: String,
      default: "btn btn-wellon-branco btn-sm"
    },

    /**
     * Recarregar a página ao trocar o estado.
     *
     * A recarga é necessária por padrão: o dado que já está na tela foi buscado
     * sob o estado anterior, e quem aplica a máscara é o servidor. Sem ela o
     * usuário libera e continua vendo a máscara, achando que não funcionou.
     *
     * Telas que sabem refazer a própria consulta passam `false` e escutam
     * `alterado`, o que evita perder filtro preenchido e resultado de uma
     * consulta cara. É o caso da prévia de campanha, que leva tempo e cujo
     * formulário se perderia inteiro numa recarga.
     */
    recarregar: {
      type: Boolean,
      default: true
    }
  },

  computed: {
    ...mapGetters([
      "currentUserPersonalInfo",
      "dadosMascarados",
      "anonimizacaoAtiva",
      "anonimizacaoLiberada",
      "anonimizacaoApresentar",
      "hasPermission",
      "telaBloqueada"
    ]),

    /**
     * Com o motor ligado, todo usuário vê o dado coberto, logo todo usuário
     * precisa do controle para liberar. Com ele desligado, o botão volta a ser
     * o Modo Apresentação de antes, que é ferramenta de demonstração e segue
     * com a permissão de sempre.
     */
    mostrarBotaoProtecao() {
      return this.anonimizacaoAtiva || this.hasPermission("modo-apresentacao");
    },

    /**
     * Vem do servidor para o texto do pedido não mentir no dia em que a
     * configuração mudar.
     */
    horasDeLiberacao() {
      return this.currentUserPersonalInfo.anonimizacao_horas || 120;
    },

    /**
     * O texto diz o ESTADO antes da ação, nesta ordem de propósito: a primeira
     * dúvida de quem chega não é o que fazer, é por que a tela está assim.
     *
     * E diz como desfazer, porque esconder o caminho de volta empurra a pessoa
     * para o suporte, que é o custo que este recurso está tentando evitar.
     */
    textoDeApoio() {
      if (this.telaBloqueada) {
        return "Tela bloqueada";
      }

      if (this.dadosMascarados) {
        return (
          "Nomes, contatos, documentos e a identificação da conta estão " +
          "ocultos nesta tela. Clique para exibi-los quando precisar: a " +
          "liberação é temporária e o acesso fica registrado."
        );
      }

      return (
        "Os dados pessoais estão visíveis nesta tela. Clique para ocultá-los, " +
        "por exemplo antes de compartilhar a tela com outra pessoa."
      );
    },

    /**
     * Pulsa só nos primeiros dias, e só com os dados cobertos.
     *
     * O recurso passa a vir ligado por padrão, e a primeira reação de quem não
     * sabe o que houve é abrir chamado. Quem decide a janela é o servidor: a
     * conta usa a data de cadastro do usuário, que a tela não tem.
     *
     * Pulso permanente numa tela que fica aberta o dia todo vira ruído em dois
     * dias, e aí a pessoa aprende a não ver o botão, que é o oposto do
     * objetivo.
     */
    deveChamarAtencao() {
      return (
        this.anonimizacaoApresentar &&
        this.dadosMascarados &&
        !this.telaBloqueada
      );
    }
  },

  methods: {
    ...mapActions(["setPresentationMode"]),

    /**
     * Cobrir é grátis, descobrir é que custa. A assimetria é o recurso: antes o
     * mesmo alerta aparecia nos dois sentidos e tirar a proteção era tão fácil
     * quanto colocá-la.
     */
    togglePresentation() {
      if (!this.dadosMascarados) {
        return this.alternarMascara(true);
      }

      // Já liberado no servidor e coberto só pelo botão: descobrir é local.
      if (!this.anonimizacaoAtiva || this.anonimizacaoLiberada) {
        return this.alternarMascara(false);
      }

      return this.liberarAnonimizacao();
    },

    /**
     * Troca o estado e manda a tela buscar os dados de novo. O que já está em
     * tela veio sob o estado anterior, e quem aplica a máscara é o servidor:
     * sem refazer a busca, o usuário libera e continua vendo a máscara.
     */
    alternarMascara(mascarar) {
      return Promise.resolve(this.setPresentationMode(mascarar)).then(() =>
        this.atualizarTela()
      );
    },

    /**
     * Recarrega, ou devolve o controle a quem sabe refazer a consulta.
     */
    atualizarTela() {
      if (this.recarregar) {
        window.location.reload();
        return;
      }

      this.$emit("alterado");
    },

    async liberarAnonimizacao() {
      // O texto diz três coisas e para por aí: por que está coberto, por quanto
      // tempo deixa de estar, e que o acesso fica registrado. A menção à LGPD é
      // o motivo, não um aviso jurídico.
      const codigo = await this.$refs.verificacaoAcao.abrir(
        "Exibir dados pessoais",
        `Dados pessoais protegidos conforme a LGPD. A liberação vale ${this.horasDeLiberacao} ` +
          "horas e o acesso fica registrado em auditoria."
      );

      if (!codigo) {
        return;
      }

      try {
        const { data } = await ApiService.post("liberar-anonimizacao", {
          codigo
        });

        this.$store.dispatch(UPDATE_PERSONAL_INFO, {
          ...this.currentUserPersonalInfo,
          ...data.data
        });

        this.setPresentationMode(false);
        this.atualizarTela();
      } catch (erro) {
        Swal.fire({
          icon: "error",
          title: "Código inválido",
          text: "Confira o código atual e tente novamente.",
          heightAuto: false
        });
      }
    }
  }
};
</script>

<style scoped>
/*
 * Pulso OCASIONAL, nao continuo: dois toques e dez segundos de silencio.
 *
 * Um pulso ininterrupto numa tela que o suporte deixa aberta o dia inteiro
 * vira ruido em dois dias, e ai a pessoa aprende a nao ver o botao, que e o
 * oposto do objetivo. O intervalo longo mantem o convite vivo sem cansar.
 *
 * A janela de dias e do servidor; aqui e so a animacao.
 */
@keyframes pulso-protecao {
  0%,
  86%,
  100% {
    box-shadow: 0 0 0 0 rgba(54, 153, 255, 0);
  }
  89% {
    box-shadow: 0 0 0 6px rgba(54, 153, 255, 0.35);
  }
  92% {
    box-shadow: 0 0 0 0 rgba(54, 153, 255, 0);
  }
  95% {
    box-shadow: 0 0 0 6px rgba(54, 153, 255, 0.25);
  }
}

.pulso-protecao {
  animation: pulso-protecao 12s ease-in-out infinite;
}

/* Quem pediu menos movimento no sistema nao deve receber um botao piscando. */
@media (prefers-reduced-motion: reduce) {
  .pulso-protecao {
    animation: none;
  }
}
</style>
